/**
 * @description 软件电话hooks
 */
import JsSIP from 'jssip'
import type { CallUserInfoVO, SipInfo } from '@/types/callCenter/callCenter'
import globalEnv from '@/configs'
import { isNullOrUndefOrEmpty } from '@/utils/is'
import { ref, toRaw } from 'vue'
import type { CallOptions, UA, UAConfiguration } from 'jssip/lib/UA'
import type { RTCSession } from 'jssip/lib/RTCSession'
import { useMedia } from './mediaUtils'
import { useCallCenter } from '@/stores/modules/callCenter'
import {
  CallingCode,
  callCenterStatusEnum,
  callDirection,
  callResult,
  callType,
  dialingScenes,
  mutedStatus,
  seatStatusEnum,
  sipErrorCode
} from '@/enums/callCenter'
import { useAccountStore } from '@/stores/modules/account'
import { callEventLoop, releaseGlobalDialLock, sipHangUp } from '@/apis/callCenter/callCenter'
import { useTurnCall } from './turnCall'
import { useCall } from './call'
import MyGlobalEvent from '@/utils/bus/baseBus'
import { syncRoundCallStatus } from '@/apis/callCenter/turnCall'
import cloudGroupCall from '@/assets/callCenter/cloudGroupCall.mp3' // 群呼来电提示音
import cloudGroupCallHangUp from '@/assets/callCenter/cloudGroupCallHangUp.mp3' // 群呼挂断提示音
import { checkOrganThreshold, getCallingCustomerInfo } from '@/apis/callCenter/cloudGroupCall'
import { useCallScreen } from '@/components/business/callCenter/callScreen/src/hooks/useCallScreen'
import { router } from '@/router'
import { getEnvMode } from '@/utils/env'

const jssipUA = ref<UA | null>(null)
const session = ref<RTCSession>()
const inSession = ref<RTCSession>()
const jssipLoopInterval = ref()
const call = ref()
const turnCall = ref()
let JSSIPUA = null

let cloudGroupCallAudio: any = null
let cloudGroupCallHangUpAudio: any = null
let isLogin = false

function replaceSipHost(value: string | undefined, fallback: string) {
  return (value || fallback)
    .replaceAll('yocwebrtc.youxinsign.com', 'fs.jyunkj.cn')
    .replaceAll('47.98.185.236', 'fs.jyunkj.cn')
}

export function useJsSIP() {
  const callCenter = useCallCenter()
  const { userInfo } = useAccountStore()
  turnCall.value = useTurnCall()
  call.value = useCall()
  /**
   * 初始化连接sip
   */
  function createJssipSocket(sipInfo: SipInfo) {
    // console.log(sipInfo, 'sipInfo')
    const webrtc = replaceSipHost(sipInfo?.webrtc, globalEnv.sip_socket_url)
    const sipHost = replaceSipHost(sipInfo?.ip, globalEnv.sip_url.replace(/^@/, ''))
    const socket = new JsSIP.WebSocketInterface(webrtc)
    const uri = `${sipInfo?.sipNum}@${sipHost}`
    const configuration: UAConfiguration = {
      sockets: [socket],
      uri: uri,
      password: sipInfo?.password,
      session_timers: true,
      contact_uri: `sip:${uri};transport=wss`,
      no_answer_timeout: 30,
      register_expires: globalEnv.sip_register_expires // 注册到期时间
    }
    jssipUA.value = new JsSIP.UA(configuration)
    // console.log('jssipUA', jssipUA.value)
    // 启动
    jssipStart()
  }

  // 启动
  function jssipStart() {
    isLogin = true
    jssipUA.value?.start()
    jssipUA.value?.on('connecting', data => {
      console.log(data, '连接中')
    })
    jssipUA.value?.on('connected', data => {
      // console.log(data, '连接成功', callCenter.dialingScenes)
      callCenter.setCallCenterStatus(callCenterStatusEnum.READY)
      getUserMedia().then(res => {
        // console.log(res, '是否有媒体权限')
        if (!res) {
          // 上报坐席状态
          callCenter.reportSeatStatus({
            status: seatStatusEnum.LEAVE,
            viewSeatStatus: callCenter.callCenterConfig?.seatExpired
              ? callCenterStatusEnum.EXPIRED
              : callCenterStatusEnum.READY
          })
        } else {
          callCenter?.reportSeatStatusTimer && clearInterval(callCenter.reportSeatStatusTimer)
          callCenter.heartBeatReportSeatStatus()
        }
      })
    })
    jssipUA.value?.on('disconnected', () => {
      console.log('连接失败')
      callCenter.setCallCenterStatus(callCenterStatusEnum.STOP)
      jssipStop()
    })
    jssipUA.value?.on('registered', data => {
      // console.log(data, '已注册')
    })
    jssipUA.value?.on('unregistered', data => {
      console.log(data, '未注册')
      isLogin &&
        callCenter.reportSeatStatus({
          status: seatStatusEnum.LEAVE,
          viewSeatStatus: callCenterStatusEnum.STOP
        })
    })
    jssipUA.value?.on('registrationFailed', data => {
      console.log(data, '注册失败')
      if (callCenter.staffDefaultCallType === callType.daco) {
        return false
      }
      callCenter.reportSeatStatus({
        status: seatStatusEnum.LEAVE,
        viewSeatStatus: callCenterStatusEnum.STOP
      })
      // store.dispatch('setCallCenterInit', false)
      // if (stopType === '') {
      //   store.dispatch('heartBeatReportStatus', {
      //     status: seatStatus.LEAVE,
      //     viewSeatStatus: viewSeatStatus.STOP
      //   })
      // }
    })
    jssipUA.value?.on('newRTCSession', (data: any) => {
      console.log(data, 'newRTCSession')
      if (data.originator === 'local') {
        return false
      }
      /**
       * 群呼来电
       */
      if (data.originator === 'remote') {
        const mediaHooks = useMedia()
        if (!isNullOrUndefOrEmpty(cloudGroupCallHangUpAudio)) {
          cloudGroupCallHangUpAudio.currentTime = 0
          cloudGroupCallHangUpAudio.pause()
          cloudGroupCallHangUpAudio = null
        }
        // 播放来电提示音
        cloudGroupCallAudio = new Audio()
        cloudGroupCallAudio.src = cloudGroupCall
        let playPro = null
        playPro = cloudGroupCallAudio.play()
        console.log(playPro)
        if (playPro !== null) {
          playPro.then(() => {
            setTimeout(() => {
              if (!isNullOrUndefOrEmpty(cloudGroupCallAudio)) {
                cloudGroupCallAudio.currentTime = 0
                cloudGroupCallAudio.pause()
              }
            }, 2000)
          })
        }
        callCenter.setCallDirection(callDirection.in)
        const requestData = data.request.data
        const map: any = {}
        requestData.split('\r\n').forEach((item: string) => {
          const index = item.indexOf(':')
          if (index >= 0) {
            const key = item.substring(0, index).trim()
            const value = item.substring(index + 1).trim()
            map[key] = value
          }
        })
        console.log(map['X-YOC-Business-ID'], '拿到了X-YOC-Business-ID')
        callCenter.setBizId(map['X-YOC-Business-ID'])
        getCallingCustomerInfo({
          bizId: map['X-YOC-Business-ID']
        }).then((res: Recordable) => {
          const userInfoVO: CallUserInfoVO = {
            title: {
              ...res,
              customerId: res.id,
              phoneCheckResult: res.phoneCheckResult || '',
              desensitizePhoneNum: res.linkPhone,
              plaintextPhone: res.plaintextPhoneNum,
              linkName: '',
              enterpriseName: res.linkName,
              realName: res.linkName,
              unknow: false
            },
            userList: [],
            gcId: res.gcId || ''
          }
          if (isNullOrUndefOrEmpty(res.customerInfoVO)) {
            // 陌生客户
            userInfoVO!.title!.unknow = true
            userInfoVO!.title!.dataFrom = 1
            userInfoVO!.title!.linkName = ''
          } else {
            userInfoVO.title = {
              ...res.customerInfoVO,
              dataFrom: res.customerInfoVO?.highSea ? 3 : 1,
              linkName: res.linkName,
              realName: res.customerInfoVO?.orderName,
              objectId: res.customerInfoVO?.orderId,
              desensitizePhoneNum: res.desensitizePhone,
              customerId: res.customerInfoVO?.orderId,
              id: res.id
            }
            // 关联客户 存储关联客户ID
            userInfoVO!.title!.unknow = false
            // userInfo!.title!.linkName = res.customerInfoVO?.orderName
            // userInfo!.title!.enterpriseName = res.customerInfoVO?.orderName
            // userInfo!.title!.realName = res.customerInfoVO?.orderName
            // userInfo!.title!.objectId = res.relateShopOrderId
            // userInfo!.title!.dataFrom = res.contactInfoVO?.highSea ? 3 : 1
            // userInfo!.title!.belongOpenSeaId = res.contactInfoVO?.highSea ? 3 : 1
          }
          console.log(userInfoVO, 'userInfo')
          callCenter.setGroupCallUserInfo(userInfoVO)
          callCenter.setShowCallScreen(true)
        })
        // 设置群呼模式
        callCenter.setdialingScenes(dialingScenes.groupCall)

        // 上报坐席状态-通话中
        callCenter.reportSeatStatus({
          status: seatStatusEnum.LEAVE,
          viewSeatStatus: callCenterStatusEnum.CALLING
        })

        // 来电
        inSession.value = data.session
        // 设置自动接听
        inSession.value?.answer({
          mediaConstraints: {
            // audio: true,
            audio: {
              // volume: {
              //   min: 0.5, max: 1, ideal: 1
              // },
              // 设置降噪
              noiseSuppression: true,
              // 设置回音消除
              echoCancellation: true
              // 声道配置
              // channelCount: 2,
              // 设置设备
              // deviceId: { ideal: store.getters.deviceId }
            },
            video: false
          },
          rtcOfferConstraints: {
            offerToReceiveAudio: true,
            offerToReceiveVideo: false
          }
        })
        // 拿到远程设置音频流
        mediaHooks.createAudio()

        // createVideo()
        const peerconnection = data.session.connection
        peerconnection.addEventListener('addstream', (event: { stream: any }) => {
          mediaHooks.setAudioStreams(event.stream)
          // setVideoStreams(event.stream)
        })
        // 来电挂断-群呼挂断
        inSession.value?.on('ended', () => {
          console.log('结束啦=========')
          console.log('ended群呼挂断', callCenter.dialingScenes)
          // callCenter.setCallCenterStatus(callCenterStatusEnum.READY)
          callCenter.setCallingCode(CallingCode.CEND)
          callCenter.setShowCallScreen(false)
          jssipUnMute()

          // 群呼挂断增加挂断提示音
          if (!isNullOrUndefOrEmpty(cloudGroupCallAudio)) {
            cloudGroupCallAudio.currentTime = 0
            cloudGroupCallAudio.pause()
          }
          cloudGroupCallHangUpAudio = new Audio()
          cloudGroupCallHangUpAudio.src = cloudGroupCallHangUp
          let playPro = null
          playPro = cloudGroupCallHangUpAudio.play()
          console.log(playPro)
          if (playPro !== null) {
            playPro.then(() => {
              setTimeout(() => {
                if (!isNullOrUndefOrEmpty(cloudGroupCallHangUpAudio)) {
                  cloudGroupCallHangUpAudio.currentTime = 0
                  cloudGroupCallHangUpAudio.pause()
                  cloudGroupCallHangUpAudio = null
                }
              }, 2000)
            })
          }
          MyGlobalEvent.$emit('cloudGroupCallWorkConsoleRefresh')
          // 判断是否是整理中
          if (callCenter.callCenterStatus === callCenterStatusEnum.CLEANUP) {
            // 整理中挂断不处理
            callCenter.setFloadMenu(true)
          } else {
            // 查询是否过期
            checkOrganThreshold({
              bizId: callCenter.bizId
            }).then(res => {
              if (res) {
                // 达到阈值设置离开
                callCenter.reportSeatStatus({
                  status: seatStatusEnum.LEAVE,
                  viewSeatStatus: callCenter.callCenterStatus
                })
              } else {
                callCenter.getCurrentCallCenterConf().then(res => {
                  callCenter.reportSeatStatus({
                    status: res.seatExpired ? seatStatusEnum.LEAVE : seatStatusEnum.FREE,
                    viewSeatStatus: res.seatExpired
                      ? callCenterStatusEnum.EXPIRED
                      : callCenterStatusEnum.READY
                  })
                })
                // store.dispatch('getCurrentCallCenterConf').then(res => {
                //   // 如果坐席过期，上报坐席过期
                //   store.dispatch('heartBeatReportStatus', {
                //     status: (webStorage.getItem('isLeave') && webStorage.getItem('isLeave') === 1) ? seatStatus.LEAVE : res.seatExpired ? seatStatus.LEAVE : seatStatus.FREE,
                //     viewSeatStatus: res.seatExpired ? viewSeatStatus.EXPIRED : viewSeatStatus.READY
                //   })
                // })
              }
            })
            callCenter.setdialingScenes(dialingScenes.singleCall)
          }
          mediaHooks.removeAudio()
        })
        inSession.value?.on('confirmed', (data: any) => {
          console.log(data, '接通')
          callCenter.setCallResult(callResult.connected)
          callCenter.setCallInResult(callResult.connected)
          callCenter.setCallCenterStatus(callCenterStatusEnum.CALLING)
          callCenter.setCallingCode(CallingCode.CBEGIN)
          // callStatus = 1
        })
        // 静音时触发
        inSession.value?.on('muted', () => {
          console.log('通话静音中')
        })

        // 取消静音时触发
        inSession.value?.on('unmuted', data => {
          console.log('恢复通话', data)
        })

        inSession.value?.on('failed', data => {
          console.log('失败', data)
        })
      }
    })
  }
  // 断开链接
  async function jssipStop() {
    isLogin = false
    if (!isNullOrUndefOrEmpty(jssipUA)) {
      JSSIPUA = toRaw(jssipUA.value)
      JSSIPUA?.stop()
      if (
        callCenter.dialingScenes === dialingScenes.turnCall ||
        callCenter.dialingScenes === dialingScenes.turnCallApp
      ) {
        ElMessage.warning('呼叫服务连接失败，轮呼终止')
        await turnCall.value.shutdown()
      }
    }
  }
  // { urls: 'stun:stun4.l.google.com:19302' },
  //     { urls: 'stun:stun2.l.google.com:19302' },
  //     { urls: 'stun:stun3.l.google.com:19302' },
  // 拨打电话
  function jssipCall(seatId: string) {
    // 初始化数据
    JSSIPUA = toRaw(jssipUA.value)
    let iceServers: RTCIceServer[] = []
    useAccountStore()?.userInfo?.tenantId === '1331111111111111177' &&
      (iceServers = [{ urls: 'stun:stun.l.google.com:19302' }])
    const callOptions: CallOptions = {
      pcConfig: { iceServers },
      mediaConstraints: {
        audio: {
          // volume: {
          //   min: 0.5, max: 1, ideal: 1
          // },
          // 设置降噪
          noiseSuppression: true,
          // 设置回音消除
          echoCancellation: true
        },
        video: false
      },
      rtcOfferConstraints: {
        offerToReceiveAudio: true,
        offerToReceiveVideo: false
      },
      sessionTimersExpires: globalEnv.sip_session_timmers_expires // 会话计时器间隔
    }
    console.log(seatId, 'seatId')
    session.value = JSSIPUA?.call(seatId, callOptions)

    const mediaHooks = useMedia()
    // 监听发起呼叫
    callCenter.setCallDirection(callDirection.out)
    session.value?.on('connecting', data => {
      // 自研线路定时任务
      jssipLoop()
      callCenter.setShowCallScreen(true)
      console.log(data, '发起呼叫')
      mediaHooks.createAudio()
      const peerconnection = session.value?.connection
      console.log('peerconnection', peerconnection)

      peerconnection?.addEventListener('addstream', (event: { stream: any }) => {
        console.log('eventevent', event)
        mediaHooks.setAudioStreams(event?.stream)
      })
      callCenter.setCallingCode(CallingCode.CORG)
      callCenter.setCallResult(callResult.startCall)
      callCenter.setCallCenterStatus(callCenterStatusEnum.CALLING)

      // store.dispatch('setCallDialogVisible', true)
    })
    session.value?.on('confirmed', (data: any) => {
      console.log(data, '接通')
    })

    session.value?.on('progress', async (data: any) => {
      console.log(data, '呼叫中')
      callCenter.setCallResult(callResult.unConnected)
      callCenter.setCallWriteFollowTag(false)
      // 自动跳转详情页
      if (
        !callCenter.callUserInfo?.title?.unknow &&
        callCenter.callUserInfo?.userList?.length === 0 &&
        callCenter.callUserInfo?.title?.dataFrom === 1
      ) {
        if (
          userInfo.id === callCenter.callUserInfo?.title.followUserId ||
          callCenter.dialingScenes === dialingScenes.singleCall
        ) {
          // if(!['/customerDetail', '/clueDetail'].includes(router.currentRoute.value.path)){
          //   callCenter.setShowCallScreen(false)
          // }
          if (callCenter.callType === 2) return
          call.value.jumpToDetail(
            callCenter.callUserInfo?.title?.customerId as string,
            callCenter.callUserInfo?.title?.realName as string
          )
        }
      }
    })

    session.value?.on('failed', async data => {
      console.log(data, '失败了')
      const reason = ref('')
      const msg = ref('')
      const isShutDown = ref(false)
      callCenter.setCallWriteFollowTag(false)
      if (data.message) {
        const exp = /text="[a-zA-Z_]*"/g
        reason.value = ("'" + data.message.headers.Reason[0].raw + "'").match(exp)[0].split('=')[1]
        console.log(reason.value, 'reason')
        sipErrorCode.filter(item => {
          if (item.code === reason.value) {
            msg.value = item.msg
            ElMessage.warning(msg.value)
            callCenter.setCallResult(callResult.failed)
            isShutDown.value = true
          }
        })

        if (reason.value === '"NO_ANSWER"' || reason.value === '"SUBSCRIBER_ABSENT"') {
          callCenter.setCallResult(callResult.startCall)
          isShutDown.value = false
        }
      } else {
        switch (data.cause) {
          case 'User Denied Media Access':
            ElMessage({
              message: '无麦克风权限，请开通相关权限',
              type: 'warning',
              offset: 64
            })
            msg.value = '无麦克风权限，请开通相关权限'
            break
          case 'Incompatible SDP':
            ElMessage.warning('配置已生效，请重新拨打')
            msg.value = '配置已生效，请重新拨打'
            isShutDown.value = false
            break
          case 'Canceled':
            msg.value = '取消呼叫'
            isShutDown.value = false
            break
          default:
            ElMessage.warning('呼叫失败')
            break
        }
      }
      if (callCenter.dialingScenes === dialingScenes.turnCall) {
        if (isShutDown.value) {
          await turnCall.value.shutdown()
          setTimeout(() => {
            ElMessage.warning('轮呼任务已终止')
          }, 0)
        }
      }
      hangUpFnc()
      mediaHooks.removeAudio()
    })

    // 静音时触发
    session.value?.on('muted', _data => {
      console.log('通话静音中')
    })

    // 取消静音时触发
    session.value?.on('unmuted', _data => {
      console.log('恢复通话')
    })

    session.value?.on('ended', () => {
      console.log('结束啦=========')
      // 重置静音状态
      jssipUnMute()
      callCenter.setMutedStatus(mutedStatus.unmute)
      hangUpFnc()
      mediaHooks.removeAudio()
    })

    session.value?.on('accepted', (_data: any) => {
      console.log('对方接通')
      const useCallScreenHooks = useCallScreen()
      // 设置
      callCenter.setCallingCode(CallingCode.CBEGIN)
      callCenter.setCallResult(callResult.connected)
      // 自动新建--排除快速拨号
      if (
        callCenter.callUserInfo?.title?.unknow &&
        callCenter.dialingScenes !== dialingScenes.quickCall
      ) {
        useCallScreenHooks.callAddCustom(callCenter.callUserInfo)
      }
    })
  }

  // 处理静音
  function jssipMute() {
    callCenter.callDirection === callDirection.out ? session.value?.mute() : inSession.value?.mute()
  }

  // 处理取消静音
  function jssipUnMute() {
    callCenter.callDirection === callDirection.out
      ? session.value?.unmute()
      : inSession.value?.unmute()
  }

  // 获取媒体设备
  async function getUserMedia() {
    let status = true
    const constrains = { video: false, audio: true }
    // console.log(navigator, 'oooo')
    if (navigator?.mediaDevices?.getUserMedia) {
      // 最新标准API
      await navigator.mediaDevices
        .getUserMedia(constrains)
        .then(stream => {
          status = true
          getDeviceListSuccess(stream)
        })
        .catch(() => {
          getUserMediaError()
          status = false
        })
    } else if (navigator?.webkitGetUserMedia) {
      // webkit内核浏览器
      await navigator
        ?.webkitGetUserMedia(constrains)
        .then((stream: any) => {
          status = true
          console.log('stream', stream)

          getDeviceListSuccess(stream)
        })
        .catch(() => {
          getUserMediaError()
          status = false
        })
    } else if (navigator?.mozGetUserMedia) {
      // Firefox浏览器
      await navigator
        ?.mozGetUserMedia(constrains)
        .then((stream: any) => {
          status = true
          getDeviceListSuccess(stream)
        })
        .catch(() => {
          getUserMediaError()
          status = false
        })
    } else if (navigator?.getUserMedia) {
      // 旧版API
      await navigator
        ?.getUserMedia(constrains)
        .then((stream: any) => {
          status = true
          getDeviceListSuccess(stream)
        })
        .catch(() => {
          getUserMediaError()
          status = false
        })
    }
    return status
  }
  function getDeviceListSuccess(stream: MediaStream) {
    const track = stream.getTracks()
    track.forEach((item: { stop: () => void }) => {
      item.stop()
    })
  }
  function getUserMediaError() {
    ElMessage({
      message: '无麦克风权限，请开通相关权限',
      type: 'error',
      offset: 64
    })
    // 上报坐席状态
    callCenter.reportSeatStatus({
      status: seatStatusEnum.LEAVE,
      viewSeatStatus: callCenter.callCenterStatus
    })
  }

  // 软件电话交换机
  function freeSwich(phone: any) {
    if (isNullOrUndefOrEmpty(callCenter.lineInfo?.lineId)) {
      ElMessage.error('请配置线路')
      return false
    }
    setSipRules(phone).then(res => {
      jssipCall(res)
    })
  }

  // 配置呼叫规则 // 线路唯一标识_被叫号码_线路前缀_后缀_20010010#AD10086EXT#15886052541#gcId
  /**
   * 配置呼叫规则
   * @param phone 被叫号码
   * @returns 配置呼叫规则
   */
  async function setSipRules(phone: string) {
    let customId: string | number = 0
    let userId = userInfo.id
    if (!userId) {
      // 线上出现了200ubndefined的情况，补救这种情况
      await useAccountStore?.()?.getUser?.()
      userId = useAccountStore()?.userInfo?.id
    }
    // customId = (this.callPhoneUserInfo.titleInfo.isContact && this.callPhoneUserInfo[0].isContact === 1) ? this.callPhoneUserInfo[0].linkInfoId : this.callPhoneUserInfo[0].objectId
    // 规则20010429-AD125260-18380317308
    //  dataType: 数据类型 1 普通客户, -1 线索', D，F
    // 普通客户A 企业客户 B 普通线索D 企业线索F AF/AD/BD/BF 普通客户线索AF 普通客户AD 企业客户线索BF 企业客户BD
    // 如果是通过普通客户联系人拨打电话，customId 末位加标识EXT
    if (
      callCenter.dialingScenes === dialingScenes.quickCall &&
      (callCenter?.callUserInfo?.title?.unknow || callCenter?.callUserInfo?.userList?.length > 0)
    ) {
      customId = 0
    } else {
      const isExt = callCenter?.callUserInfo?.title?.bizType === 3 ? 'EXT' : ''
      customId = `AD${callCenter?.callUserInfo?.title?.objectId}${isExt}`
    }

    const rules = `200${userId}#${customId}#${callCenter?.callUserInfo?.gcId}#${phone}`
    const jssipCallId = `${callCenter.lineInfo?.lineId}_${phone}_${
      callCenter.lineInfo?.prefix || ''
    }_${callCenter.lineInfo?.suffix || ''}_${rules}`
    console.log(jssipCallId, '自研线路规则')
    callCenter.setJssipCallId(rules)
    return jssipCallId
  }

  // 挂断
  function jssipHangUp() {
    console.log('挂断', callCenter.callDirection)
    callCenter.setCallWriteFollowTag(false)
    if (!isNullOrUndefOrEmpty(session.value) && callCenter.callDirection === callDirection.out) {
      const sessionV = toRaw(session.value)
      sessionV?.terminate?.()
    }
    if (!isNullOrUndefOrEmpty(inSession.value) && callCenter.callDirection === callDirection.in) {
      const inSessionV = toRaw(inSession.value)
      console.log(inSessionV)
      inSessionV?.terminate?.()
    }
  }

  // 挂断后操作
  async function hangUpFnc() {
    const successFn = () => {
      callCenter.setShowCallScreen(false)
      callCenter.setCallCenterStatus(callCenterStatusEnum.READY)
    }
    const useCallScreenHooks = useCallScreen()
    // 释放通话锁
    releaseGlobalDialLock()
    clearInterval(jssipLoopInterval.value)
    sipHangUp({
      callId: callCenter.jssipCallId
    })
    if (callCenter.dialingScenes === dialingScenes.quickCall) {
      // 快速呼叫挂断
      successFn()
      // 如果没有新建，则弹出新建
      if (
        !callCenter.quickCallAdd &&
        callCenter.callUserInfo?.userList?.length === 0 &&
        callCenter.callUserInfo?.title?.unknow &&
        callCenter.callResult === callResult.connected
      ) {
        useCallScreenHooks.callAddCustom(callCenter.callUserInfo)
      }
    }
    // 单呼挂断
    if (callCenter.dialingScenes === dialingScenes.singleCall) {
      successFn()
    }
    // 轮呼挂断
    if (
      callCenter.dialingScenes === dialingScenes.turnCall ||
      callCenter.dialingScenes === dialingScenes.turnCallApp
    ) {
      callCenter.setShowCallScreen(false)
      callCenter.setCallCenterStatus(callCenterStatusEnum.READY)
      // 更新轮呼客户通话状态
      await syncRoundCallStatus({
        id: callCenter.callUserInfo?.title?.roundCustomerId,
        callStatus: callCenter.callResult,
        version: 160
      })
      MyGlobalEvent.$emit('roundCallDetailRefresh')
      if (callCenter.callResult === callResult.unConnected) {
        // 轮呼时，无论通话是否接通，系统打开详情页后，需手动点击呼叫下一个才弹出等待弹窗，在客户详情页未点击呼叫下一个时不会触发拨打下一个
        if (['/customerDetail', '/clueDetail'].includes(router.currentRoute.value.path)) {
          ElMessage.warning('通话未接通')
          callCenter.setFloadMenu(true)
        } else {
          // 未接通，自动轮呼下一个
          ElMessage.warning('通话未接通，即将呼叫下一个')
          turnCall.value.showCountDown()
        }
      }
      if (callCenter.callResult === callResult.failed) {
        ElMessage.warning('轮呼已经终止了')
      }
      if (callCenter.callResult === callResult.connected) {
        // 已接通，显示悬浮窗
        callCenter.setFloadMenu(true)
      }
      // // 挂载中不操作
      // if(callCenter.callCenterStatus !== callCenterStatusEnum.CLEANUP) {
      //   if(callCenter.callResult === callResult.failed) {
      //     ElMessage.warning('轮呼已经终止了')
      //   } else {
      //     turnCall.value.showCountDown()
      //   }
      // }

      // if(callCenter.callResult === callResult.unConnected) {
      //   turnCall.value.showCountDown()
      //   ElMessage.warning('通话未接通，即将呼叫下一个')
      // }
    }
  }

  // 自研线路定时任务
  function jssipLoop() {
    jssipLoopInterval.value = setInterval(() => {
      callEventLoop({
        callId: callCenter.jssipCallId
      })
    }, 60000)
  }

  return {
    createJssipSocket,
    jssipStart,
    jssipStop,
    jssipCall,
    jssipMute,
    jssipUnMute,
    getUserMedia,
    freeSwich,
    jssipHangUp,
    setSipRules
  }
}
